import { useCallback, useEffect, useRef, useState } from 'react';
import type { CardId, Move, Seat, Suit } from '@ubs/rules';
import {
  FALLBACK_CHAT_PHRASES,
  TRICK_HOLD_MS,
  eventDuration,
  type GameEvent,
  type PublicSeat,
  type ViewMessage,
} from '@ubs/protocol';
import { applyEvent, initialDisplay, reconcile, type DisplayState } from '../game/display.js';
import { createLocalGame } from './localGame.js';
import { clearLocal, loadLocal } from './localStore.js';
import { connectOnline } from './online.js';
import type { ClientMessageType, Transport, TransportHandlers } from './transport.js';

export type Status = 'idle' | 'connecting' | 'lobby' | 'playing' | 'over' | 'error';
export type Mode = 'local' | 'online';

/** Hazır mesaj balonunun ekranda kalma süresi. */
const CHAT_VISIBLE_MS = 3600;
/** Tek geçerli kart varken otomatik oynamadan önceki bekleme. */
const AUTO_PLAY_MS = 600;
/** Otomatik oyna tercihi cihazda kalıyor: her maçta yeniden ayarlanmasın. */
const AUTO_PLAY_KEY = 'ubs:autoplay';

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface GameApi {
  status: Status;
  mode: Mode | null;
  error: string | null;
  /** Yetkili durum — faz, skor ve paneller buradan okunuyor. ANİMASYON
   * KUYRUĞU KADAR GERİDE: ekranda görüneni anlatıyor. */
  view: ViewMessage | null;
  /**
   * Sunucudan gelen EN SON durum, kuyruğu beklemeden.
   *
   * Yalnızca girdi kapısı için: hangi kartın oynanabileceği ekrandaki
   * animasyona değil gerçek duruma bağlı olmalı. Aksi hâlde sıra bana
   * geçtiğinde bile son animasyon bitene kadar kart atamıyordum.
   */
  live: ViewMessage | null;
  /** Ekranda görünen durum — animasyon kuyruğu kadar geride. */
  display: DisplayState;
  seats: PublicSeat[];
  /** Masada oturanların adları (kartlar dağıtılmadan önce). */
  tablePlayers: string[];
  /** Masa doldu; maçın başlayacağı mutlak an (epoch ms). Yoksa null. */
  startsAt: number | null;
  /** Kuyruk oynarken girdi kilitleniyor; yarım animasyona hamle gönderilmiyor. */
  busy: boolean;
  /**
   * Bu faz için seçimimi gönderdim, karşılık bekliyorum. Arayüz düğmeyi
   * hemen kilitliyor — tekrar basılması aynı hamleyi iki kez yollardı.
   */
  sent: boolean;
  /**
   * Tek geçerli kartın kendiliğinden oynanması açık mı.
   *
   * Renk takibi ve yükseltme zorunluluğu yüzünden çoğu turda oynanacak tek
   * kart kalıyor; onu da dokunup atmak boş bir adım. Kapalıyken karar yine
   * oyuncunun.
   */
  autoPlay: boolean;
  toggleAutoPlay(): void;
  /** Maç başladı mı (ilk dağıtım yapıldı mı). */
  started: boolean;
  /** "Oyuna başla" düğmesi gösterilsin mi — yalnızca botlara karşı. */
  canStart: boolean;
  startMatch(): void;
  /** Şu an ekranda duran hazır mesajlar: koltuk → cümle. */
  chat: Partial<Record<Seat, string>>;
  phrases: string[];
  sendChat(text: string): void;
  /** `resume` verilirse yarım kalmış maçtan devam edilir. */
  startLocal(name: string, resume?: boolean): void;
  connect(name: string, code: string, pass: string): Promise<void>;
  play(card: CardId): void;
  chooseTrump(suit: Suit): void;
  bury(cards: CardId[]): void;
  exchange(gives: { to: Seat; cards: CardId[] }[]): void;
  quit(): void;
}

/**
 * Gönderilmiş hamlenin kimliği.
 *
 * Faz, el ve tamamlanmış alışveriş sayısı birlikte tek bir adımı işaretliyor.
 * Sunucu hamleyi işlediği an bu anahtar değişiyor, yani "gönderildi" durumu
 * kendiliğinden sıfırlanıyor — ayrıca temizlemek gerekmiyor.
 */
const actionKey = (view: ViewMessage): string =>
  `${view.dealIndex}|${view.phase}|${view.myExchange.length}`;

export function useGame(): GameApi {
  const [status, setStatus] = useState<Status>('idle');
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMessage | null>(null);
  const [live, setLive] = useState<ViewMessage | null>(null);
  const [started, setStarted] = useState(false);
  const [display, setDisplay] = useState<DisplayState>(initialDisplay);
  const [seats, setSeats] = useState<PublicSeat[]>([]);
  const [tablePlayers, setTablePlayers] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentKey, setSentKey] = useState<string | null>(null);
  const [chat, setChat] = useState<Partial<Record<Seat, string>>>({});
  const [phrases, setPhrases] = useState<string[]>([...FALLBACK_CHAT_PHRASES]);
  /**
   * VARSAYILAN KAPALI: oyunun kararlarını sessizce devralan bir ayar açık
   * gelmemeli. Oyuncu isterse açıyor, tercih cihazda kalıyor.
   */
  const [autoPlay, setAutoPlay] = useState(() => {
    try {
      return localStorage.getItem(AUTO_PLAY_KEY) === '1';
    } catch {
      return false; // gizli sekmede depolama kapalı olabilir
    }
  });
  const toggleAutoPlay = useCallback(() => {
    setAutoPlay((on) => {
      const next = !on;
      try {
        localStorage.setItem(AUTO_PLAY_KEY, next ? '1' : '0');
      } catch {
        /* depolama yoksa tercih yalnızca bu oturumda geçerli */
      }
      return next;
    });
  }, []);

  const transport = useRef<Transport | null>(null);
  const queue = useRef<GameEvent[]>([]);
  const pendingView = useRef<ViewMessage | null>(null);
  const draining = useRef(false);
  const me = useRef<Seat>(0);
  /** İlk görünüm geldi mi — geldiyse kuyruk kuralı yeniden geçerli. */
  const hasView = useRef(false);
  const chatTimers = useRef(new Map<Seat, ReturnType<typeof setTimeout>>());

  const showChat = useCallback((seat: Seat, text: string) => {
    clearTimeout(chatTimers.current.get(seat));
    setChat((current) => ({ ...current, [seat]: text }));
    chatTimers.current.set(
      seat,
      setTimeout(() => {
        setChat((current) => {
          const next = { ...current };
          delete next[seat];
          return next;
        });
        chatTimers.current.delete(seat);
      }, CHAT_VISIBLE_MS),
    );
  }, []);

  /**
   * Yeni elin kartlarını DAĞITIM ANINDA ele koyar.
   *
   * `dealStart` eli boşaltıyor, gerçek kartlar ise kuyruk bitince gelen
   * görünümle geliyordu — el sonu özeti (2.2 sn) ve alışveriş açılışı
   * araya girdiği için kartlar saniyeler sonra beliriyordu. Artık dağıtım
   * olayı oynarken bekleyen görünümden okunuyor.
   */
  const revealHand = useCallback(() => {
    const latest = pendingView.current;
    if (!latest) return;
    setDisplay((current) => ({
      ...current,
      hand: [...latest.hand],
      counts: [...latest.handCounts],
    }));
  }, []);

  /** Kuyruğu sırayla oynatır; bitince yetkili görünüme oturur. */
  const drain = useCallback(async () => {
    if (draining.current) return;
    draining.current = true;
    setBusy(true);

    while (queue.current.length > 0) {
      const event = queue.current.shift()!;

      /**
       * Tur kapanışı İKİ AŞAMA: önce üç kart olduğu gibi duruyor, sonra
       * kazanana doğru süpürülüyor.
       *
       * Süpürmeyi tetikleyen şey `trickWinner` alanının dolması, yani bu
       * olayın uygulanması. Uygulamayı geciktirmek duraklamayı veriyor;
       * bekleme olayın kendi süresinin içinden geliyor, o yüzden sunucunun
       * sıra zamanlaması da kaymıyor.
       */
      if (event.kind === 'trickEnd') await pause(TRICK_HOLD_MS);

      setDisplay((current) => applyEvent(current, event, me.current));
      /**
       * Mikro göreve alınıyor: `drain` ilk olayı SENKRON işliyor ve o an
       * bekleyen görünüm henüz gelmemiş oluyor (taşıma katmanı önce olayları,
       * sonra görünümü veriyor). Bir mikro görev sonra içerik yerinde.
       */
      if (event.kind === 'exchangeStep' || event.kind === 'dealStart') {
        queueMicrotask(revealHand);
      }
      const wait =
        eventDuration(event) - (event.kind === 'trickEnd' ? TRICK_HOLD_MS : 0);
      if (wait > 0) await pause(wait);
    }

    const latest = pendingView.current;
    if (latest) {
      pendingView.current = null;
      setView(latest);
      setDisplay((current) => reconcile(current, latest));
      setSeats(latest.seats);
    }

    draining.current = false;
    setBusy(false);
  }, [revealHand]);

  const handlers = useRef<TransportHandlers>({
    event(event) {
      /**
       * Sohbet KUYRUĞA GİRMİYOR. Kuyruk kart animasyonlarını sıraya diziyor;
       * mesaj oranın arkasında beklerse saniyeler sonra beliriyor ve neye
       * cevap verildiği anlaşılmıyor.
       */
      if (event.kind === 'chat') {
        showChat(event.seat, event.text);
        return;
      }
      if (event.kind === 'matchStart') {
        setSeats(event.seats);
        setStatus('playing');
        setStartsAt(null);
        setStarted(true);
      }
      if (event.kind === 'matchOver') setStatus('over');
      queue.current.push(event);
      void drain();
    },
    view(incoming) {
      me.current = incoming.you;
      // Girdi kapısı ANINDA güncelleniyor; ekran kuyruğun ardından gelecek.
      setLive((current) => (current && current.seq > incoming.seq ? current : incoming));
      if (!pendingView.current || incoming.seq > pendingView.current.seq) {
        pendingView.current = incoming;
      }

      /**
       * İLK görünüm kuyruğu beklemiyor. Kuyruğun bitmesini beklemenin sebebi
       * yarım animasyonun üstüne yazmamak; ortada henüz bir animasyon durumu
       * yokken beklemek yalnızca boş ekran demek.
       */
      if (!hasView.current) {
        hasView.current = true;
        pendingView.current = null;
        setView(incoming);
        setDisplay((current) => reconcile(current, incoming));
        setSeats(incoming.seats);
      }

      setStatus((s) => (s === 'lobby' || s === 'connecting' ? 'playing' : s));
      void drain();
    },
    error(message) {
      setError(message);
      setTimeout(() => setError(null), 2600);
    },
    lobby(players) {
      setTablePlayers(players);
      setStatus('lobby');
    },
    starting(at) {
      setStartsAt(at);
    },
    phrases(list) {
      if (list.length > 0) setPhrases(list);
    },
  });

  const reset = useCallback(() => {
    transport.current?.dispose();
    transport.current = null;
    queue.current = [];
    pendingView.current = null;
    draining.current = false;
    hasView.current = false;
    for (const timer of chatTimers.current.values()) clearTimeout(timer);
    chatTimers.current.clear();
    setChat({});
    setStartsAt(null);
    setTablePlayers([]);
    setDisplay(initialDisplay());
    setView(null);
    setLive(null);
    setStarted(false);
    setSeats([]);
    setError(null);
    setSentKey(null);
  }, []);

  const startLocal = useCallback(
    (name: string, resume = false) => {
      reset();
      setMode('local');
      setStatus('connecting');
      const saved = resume ? loadLocal() : null;
      // Yeni oyun eski kaydı geçersiz kılıyor: iki maç birden saklanmıyor.
      if (!saved) clearLocal();
      transport.current = createLocalGame(
        { name: name || 'Sen', resume: saved ?? undefined },
        handlers.current,
      );
    },
    [reset],
  );

  const connect = useCallback(
    async (who: string, code: string, pass: string) => {
      reset();
      setMode('online');
      setStatus('connecting');
      try {
        transport.current = await connectOnline({ name: who, code, pass }, handlers.current);
        setStatus('lobby');
      } catch (cause) {
        // Bağlanamadıysak salona geri dönüyoruz: 'error' durumu ekranı boş
        // bırakıyor, oyuncunun elinde tekrar deneyecek bir düğme kalmıyordu.
        setMode(null);
        setStatus('idle');
        setError(cause instanceof Error ? cause.message : 'Bağlanılamadı');
        setTimeout(() => setError(null), 2600);
      }
    },
    [reset],
  );

  const send = useCallback((type: ClientMessageType, payload: unknown) => {
    transport.current?.send(type, payload);
  }, []);

  /** Hamleyi yollarken bu adımı "gönderildi" diye işaretler. */
  const submit = useCallback(
    (type: 'move' | 'trump' | 'bury' | 'exchange', payload: unknown) => {
      if (view) setSentKey(actionKey(view));
      send(type, payload);
    },
    [send, view],
  );

  const play = useCallback(
    (card: CardId) => send('move', { type: 'play', card } satisfies Move),
    [send],
  );
  const chooseTrump = useCallback((suit: Suit) => submit('trump', { suit }), [submit]);
  const bury = useCallback((cards: CardId[]) => submit('bury', { cards }), [submit]);
  const exchange = useCallback(
    (gives: { to: Seat; cards: CardId[] }[]) => submit('exchange', { gives }),
    [submit],
  );
  const sendChat = useCallback((text: string) => send('chat', { text }), [send]);
  const startMatch = useCallback(() => send('start', {}), [send]);

  /**
   * Tek geçerli kart varsa kendiliğinden oyna.
   *
   * `view`e bakıyor, `live`e değil: kuyruk oynarken tetiklenirse oyuncu ne
   * olduğunu göremeden kart gidiyor. `seq` başına bir kez — aynı görünüm
   * yeniden ulaşırsa ikinci kez oynamasın.
   */
  const autoPlayedSeq = useRef(-1);
  useEffect(() => {
    if (!autoPlay || !view || busy) return;
    if (view.phase !== 'play' || view.turn !== view.you) return;
    if (view.legalCards.length !== 1) return;
    if (autoPlayedSeq.current === view.seq) return;

    autoPlayedSeq.current = view.seq;
    const card = view.legalCards[0]!;
    const id = setTimeout(() => play(card), AUTO_PLAY_MS);
    return () => clearTimeout(id);
  }, [autoPlay, view, busy, play]);

  const quit = useCallback(() => {
    reset();
    setMode(null);
    setStatus('idle');
  }, [reset]);

  useEffect(() => () => transport.current?.dispose(), []);

  return {
    status,
    mode,
    error,
    view,
    display,
    seats,
    tablePlayers,
    startsAt,
    busy,
    sent: view !== null && sentKey === actionKey(view),
    live,
    autoPlay,
    toggleAutoPlay,
    started,
    // Online masada başlangıç sunucunun işi (masa dolunca başlıyor); düğme
    // yalnızca botlara karşı anlamlı.
    canStart: mode === 'local' && !started && startsAt === null && view !== null,
    startMatch,
    chat,
    phrases,
    sendChat,
    startLocal,
    connect,
    play,
    chooseTrump,
    bury,
    exchange,
    quit,
  };
}
